import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2, Webhook } from 'lucide-react';
import type { PlaidWebhookRegistrationResponse } from '@signalvote/shared';
import { useAuth } from '@/contexts/AuthContext';
import { authService } from '@/services/authService';
import { API_BASE_URL } from '@/config';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

/**
 * Platform-admin switch for banks linked before Plaid webhooks existed. A new link registers the webhook
 * itself; this points every already-linked bank in this brand (every campaign) at it, once, after the
 * webhook base URL is configured. Re-running it is harmless.
 */
export function PlaidWebhookSwitchCard() {
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const { mutate: runSwitch, isPending, data } = useMutation({
    mutationFn: async (): Promise<PlaidWebhookRegistrationResponse> => {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/api/finance/backfill/plaid-webhooks`, {
        method: 'POST',
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message ?? 'The switch failed');
      }
      const result: PlaidWebhookRegistrationResponse = await response.json();
      return result;
    },
    onSuccess: () => setError(null),
    onError: (e: Error) => setError(e.message),
  });

  if (!user?.isPlatformAdmin) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Webhook className="h-4 w-4" />
          Plaid webhooks for linked banks
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Banks linked before webhooks were turned on update once a day. Switch them so Plaid tells us about
          new transactions as they happen. Run it once after the webhook URL is configured; running it again is
          harmless.
        </p>
        <Button size="sm" onClick={() => runSwitch()} disabled={isPending}>
          {isPending ? (
            <>
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              Switching…
            </>
          ) : (
            'Switch banks to webhooks'
          )}
        </Button>
        {data && !error && (
          <div className="text-sm">
            <p>
              Switched {data.registered} {data.registered === 1 ? 'bank' : 'banks'}.
            </p>
            {data.failedConnectionIds.length > 0 && (
              <p className="text-red-600">
                Plaid refused {data.failedConnectionIds.length}; they keep updating daily:{' '}
                {data.failedConnectionIds.join(', ')}
              </p>
            )}
          </div>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
      </CardContent>
    </Card>
  );
}
